import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormState } from 'react-hook-form'
import MessageCard from '@groovehq/internal-design-system/lib/components/MessageCard/MessageCard'
import {
  text,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import ProgressDots from '@groovehq/internal-design-system/lib/components/ProgressDots/ProgressDots'
import { useDrawer } from 'ducks/drawers/hooks'

import {
  doSaveChannelDraft,
  doClearChannelDraft,
  doSyncChannelDraft,
  doAddGmailChannel,
} from 'ducks/channels/actions'
import { useConfirmHoldsCallback } from 'util/dirtyHolds'
import { oAuthErrorToErrorCode } from 'subapps/settings/components/drawers/Channels/ChannelDetails/oAuthError'
import ChannelDetailsForm from 'subapps/settings/components/shared/forms/ChannelDetails'
import { isBridgeChannelType } from 'ducks/channels/channelTypes'
import {
  useRedirectToChannel,
  useChannelDetailsConfig,
  useRebuildLeftNavMenu,
} from 'ducks/channels/hooks'
import { selectIsInInbox } from 'selectors/location'
import { selectAgentCount } from 'selectors/agents/base'
import { DRAWER_TYPE_CHANNELS_MEMBERS_EDIT_MODAL } from 'ducks/drawers/types'
import { DRAWER_ID_FOLDERS_CHANNELS_MEMBERS_EDIT } from 'ducks/drawers/ids'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'
import { CHANNEL_TYPES } from '../../Channels.data'
import { styles } from '../styles'
import { useAdminAccess } from '../../../NoAccess/hooks'

const Actions = ({
  formId,
  channelType,
  onClose,
  isInstalling,
  errorCode,
  control,
  firstStep,
  isOAuthSccessful,
}) => {
  const { name, icon } = CHANNEL_TYPES[channelType]
  const { oAuthErrors, oAuthSuccess } = useChannelDetailsConfig(channelType)

  const { isValid } = useFormState({ control })
  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const oauthErrorMessage = useMemo(
    () => {
      if (!errorCode) return null

      return oAuthErrors[errorCode] || oAuthErrors.DEFAULT
    },
    [errorCode, oAuthErrors]
  )

  const isAuthenticate = !isBridgeChannelType(channelType)

  const saveBtnText = (
    <>
      {isAuthenticate && (
        <>
          <span css={[styles.btnIcon, styles.btn]} className="grui mr-4">
            {icon}
          </span>
          Connect your {name} account
        </>
      )}
      {!isAuthenticate && 'Next step'}
    </>
  )

  return (
    <>
      <Modal.Button
        type="info"
        htmlType="submit"
        data-test-id="authenticate-google-button"
        disabled={!isValid || isInstalling}
        form={formId}
        css={styles.oauthBtn}
        backdropTransparency="light"
      >
        {saveBtnText}
      </Modal.Button>
      {oauthErrorMessage && (
        <MessageCard
          type="negative"
          closeable
          className="grui my-10 flex-center"
          css={styles.messageCard}
          isIconHidden
        >
          <p css={[paragraph.styles.preflight, text.styles.fontMedium]}>
            Authentication error
          </p>
          <p css={paragraph.styles.preflight} className="grui mt-4">
            {oauthErrorMessage}
          </p>
        </MessageCard>
      )}
      {isOAuthSccessful && (
        <MessageCard
          type="positive"
          className="grui my-10 flex-center"
          css={[
            paragraph.styles.preflight,
            text.styles.fontMedium,
            styles.messageCard,
          ]}
        >
          {oAuthSuccess}
        </MessageCard>
      )}
      {!firstStep && (
        <Modal.Button type="link" className="grui mt-11" onClick={handleBack}>
          Back
        </Modal.Button>
      )}
    </>
  )
}

// shares some functionality with drawers/Channels/AuthenticateChannel
const OAuthModal = ({
  onClose,
  onExit,
  drawerChannelType: channelType = 'forwarding',
  drawerResourceId: channelId,
  drawerId,
  firstStep,
}) => {
  const isInInbox = useSelector(selectIsInInbox)
  const dispatch = useDispatch()
  const [errorCode, setErrorCode] = useState(null)
  const [isInstalling, setIsInstalling] = useState(false)
  const [nextDrawerResourceId, setNextDrawerResourceId] = useState(null)
  const [isOAuthSccessful, setIsOAuthSccessful] = useState()
  const activeAgentCount = useSelector(selectAgentCount)
  const { name } = CHANNEL_TYPES[channelType]
  const timeoutRef = useRef()

  const {
    drawerId: editMembersDrawerId,
    openDrawer: openEditMembers,
  } = useDrawer({
    id: DRAWER_ID_FOLDERS_CHANNELS_MEMBERS_EDIT,
    type: DRAWER_TYPE_CHANNELS_MEMBERS_EDIT_MODAL,
    closeIgnoresStack: false,
  })

  const formId = `${drawerId}-form`

  useAdminAccess(onClose)

  const { rebuildExitAndRedirect } = useRedirectToChannel({
    channelId: nextDrawerResourceId, // channel id after mailbox is created
    onExit,
  })

  const { rebuildMenuAndExit } = useRebuildLeftNavMenu({
    channelId: nextDrawerResourceId, // channel id after mailbox is created
    onExit,
  })

  const handleOnClose = useConfirmHoldsCallback(formId, onClose, [onClose])

  const handleOnExit = useConfirmHoldsCallback(
    null,
    () => {
      dispatch(doClearChannelDraft(channelId))
      // we do not rebuild left nav as soon as channel is created anymore
      // if the user clicks the Exit button on top right, rebuild nav
      if (isInInbox) {
        rebuildExitAndRedirect()
        return
      }
      rebuildMenuAndExit()
    },
    [dispatch, onExit, channelId, isInInbox]
  )

  const handleError = useCallback(
    error => {
      setErrorCode(oAuthErrorToErrorCode(error, channelType))
      // eslint-disable-next-line no-console
      console.error(error)
    },
    [channelType]
  )

  const handleGmailChannel = useCallback(
    async data => {
      const res = await dispatch(doAddGmailChannel(data))
      dispatch(doBuildInboxMenuFromMailboxes())
      return res
    },
    [dispatch]
  )

  const handleChannelOAuth = useCallback(
    data => {
      switch (channelType) {
        case 'google':
          return handleGmailChannel(data)
        default:
          return null
      }
    },
    [channelType, handleGmailChannel]
  )

  const onSubmit = useCallback(
    async data => {
      try {
        setIsInstalling(true)
        setErrorCode(null)

        const {
          entities: {
            pending: { update: { channel: channelById } = {} } = {},
          } = {},
        } = dispatch(doSaveChannelDraft(channelId, data))

        const { id } = await handleChannelOAuth(channelById[channelId])

        // clear 'new' draft
        dispatch(doClearChannelDraft('new'))
        // copy over created channel from current to pending store
        dispatch(doSyncChannelDraft(id))

        setIsOAuthSccessful(true)
        timeoutRef.current = setTimeout(() => {
          setNextDrawerResourceId(id)
          timeoutRef.current = null
        }, 1000)
      } catch (error) {
        setIsInstalling(false)
        handleError(error)
      }
    },
    [dispatch, channelId, handleChannelOAuth, handleError]
  )

  useEffect(
    () => {
      if (!nextDrawerResourceId) return

      handleOnExit()
    },
    [
      nextDrawerResourceId,
      activeAgentCount,
      openEditMembers,
      editMembersDrawerId,
      channelType,
      handleOnExit,
    ]
  )

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const actionsComponentProps = useMemo(
    () => {
      return {
        formId,
        onClose: handleOnClose,
        channelType,
        isInstalling,
        errorCode,
        firstStep,
        isOAuthSccessful,
      }
    },
    [
      formId,
      handleOnClose,
      channelType,
      isInstalling,
      errorCode,
      firstStep,
      isOAuthSccessful,
    ]
  )

  const footerRef = useRef(null)

  return (
    <Modal
      onClose={handleOnExit}
      dataTestId="channels-details-oauth"
      portal="#drawer-root"
      open
    >
      <ProgressDots
        count={CHANNEL_TYPES[channelType].steps}
        now={2}
        className="grui mt-4 mb-13"
      />
      <Modal.Title>Connect your {app.t('Mailbox')}</Modal.Title>
      <Modal.Description css={styles.description}>
        To start receiving your email in Groove, we need to link your
        {` ${name} `}account that you use to chat to your customers.
      </Modal.Description>
      <div css={styles.content} className="grui pt-5 pb-6 text-center">
        <ChannelDetailsForm
          formId={formId}
          channelId={channelId}
          channelType={channelType}
          onSubmit={onSubmit}
          fullWidth
          actionsPortalRef={footerRef}
          actionsComponent={Actions}
          actionsComponentAdditionalProps={actionsComponentProps}
        />
        <div ref={footerRef} className="grui mt-20" />
      </div>
    </Modal>
  )
}

export default OAuthModal
